import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import type { OrderForm } from 'src/sdk/checkout/data/types/orderform'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import type { OrderForm as CheckoutOrderForm } from 'src/sdk/checkout/entity/orderForm'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { HAVE_WINDOW } from 'src/constants'

import { useAddressManagerController } from '../../Hooks/useAddressManagerController'
import { AddressManagerEditorSection } from './AddressManagerEditorSection'
import { AddressManagerListSection } from './AddressManagerListSection'
import { ManageAddressesSheetHeader } from './ManageAddressesSheetHeader'

const useIsomorphicLayoutEffect = HAVE_WINDOW ? useLayoutEffect : useEffect

interface AddressManagerModalProps {
  addresses: AddressData[]
  orderForm: OrderForm
  setOrderForm: (orderForm: CheckoutOrderForm) => void
  defaultAddressId: string | null
  setDefaultAddressId: Dispatch<SetStateAction<string | null>>
  selectedAddressId: string | undefined | null
  onSelectedAddressChange?: (addressId: string | null) => void
  setPostalCode: (postalCode: string) => void
  setAddress: Dispatch<SetStateAction<Address>>
}
export function AddressManagerModal({
  addresses,
  orderForm,
  setOrderForm,
  defaultAddressId,
  setDefaultAddressId,
  selectedAddressId,
  onSelectedAddressChange,
  setPostalCode,
  setAddress,
}: AddressManagerModalProps) {
  const {
    editingAddress,
    addingAddress,
    newCep,
    setNewCep,
    sortedAddresses,
    localDefaultAddressId,
    localSelectedAddressId,
    addressesCountLabel,
    addressFormModalKey,
    exitAddressEditor,
    handleFormSyncLocalAddresses,
    handleCardSyncLocalAddresses,
    handleNewCepSubmit,
    startAddingAddress,
    setEditingAddress,
    handleSelectAddress,
    handleMakeDefaultAddress,
  } = useAddressManagerController({
    addresses,
    defaultAddressId,
    selectedAddressId,
    onSelectedAddressChange,
    setDefaultAddressId,
    setPostalCode,
    setAddress,
  })

  const isListState = !editingAddress && !addingAddress

  // Keep a ref to the modal-sheet context so the sync effect below only
  // depends on `isListState` (context callbacks are recreated on every
  // provider render and would otherwise re-trigger this effect in a loop).
  const modalSheet = useModalSheet()
  const modalSheetRef = useRef(modalSheet)

  modalSheetRef.current = modalSheet

  const handleBackToPreviousModal = useCallback(() => {
    modalSheetRef.current.closeModal()
  }, [])

  useIsomorphicLayoutEffect(() => {
    modalSheetRef.current.setModalConfig((prevConfig) => ({
      ...prevConfig,
      header: {
        isVisible: true,
        component: (
          <ManageAddressesSheetHeader
            onBack={isListState ? handleBackToPreviousModal : undefined}
          />
        ),
      },
    }))
  }, [isListState, handleBackToPreviousModal])

  if (editingAddress || addingAddress) {
    return (
      <AddressManagerEditorSection
        addingAddress={addingAddress}
        newCep={newCep}
        setNewCep={setNewCep}
        onBack={exitAddressEditor}
        onNewCepSubmit={handleNewCepSubmit}
        addressFormModalKey={addressFormModalKey}
        data={editingAddress}
        orderForm={orderForm}
        setOrderForm={setOrderForm}
        onUpdateLocalAddresses={handleFormSyncLocalAddresses}
      />
    )
  }

  return (
    <AddressManagerListSection
      addressesCountLabel={addressesCountLabel}
      sortedAddresses={sortedAddresses}
      localDefaultAddressId={localDefaultAddressId}
      localSelectedAddressId={localSelectedAddressId}
      orderFormId={orderForm.orderFormId}
      syncOrderFormFn={setOrderForm}
      onEditAddress={setEditingAddress}
      onSelectAddress={handleSelectAddress}
      onMakeDefaultAddress={handleMakeDefaultAddress}
      onUpdateLocalAddresses={handleCardSyncLocalAddresses}
      onStartAddingAddress={startAddingAddress}
    />
  )
}
